"use client";

import { useLayoutEffect, useRef } from "react";

import {
  getTitrationCalculation,
  selectedTitrationFlow,
  titrationTapSettingLabel,
  TitrationGuidedStep,
  type TitrationGuidedAction,
  type TitrationGuidedState,
  type TitrationGuidedStepDefinition,
} from "./TitrationGuidedController";
import styles from "./titration-guided-card.module.css";

export type TitrationGuidedCardProps = {
  state: TitrationGuidedState;
  definition: TitrationGuidedStepDefinition;
  readingMl: number;
  onDispatch: (action: TitrationGuidedAction) => void;
  onStepAway: () => void;
  onReadMeniscus?: () => void;
  onPracticeReading?: () => void;
};

export default function TitrationGuidedCard({
  state,
  definition,
  readingMl,
  onDispatch,
  onReadMeniscus,
  onPracticeReading,
}: TitrationGuidedCardProps) {
  const flow =
    state.session.tapOpenFraction > 0
      ? state.session.tapOpenFraction
      : selectedTitrationFlow(state.session);
  const reviewMode = state.reviewIndex !== null;
  const latestCheckpoint = state.checkpoints.at(-1) ?? null;
  const calculation = getTitrationCalculation(state.session);
  const showCalculation =
    !reviewMode &&
    definition.id === "calculate" &&
    state.calculationOpen &&
    calculation;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focusedControl = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const previous = focusedControl.current;
    if (previous && (!previous.isConnected || previous.matches(":disabled"))) {
      if (
        document.activeElement === document.body ||
        document.activeElement === previous
      ) {
        headingRef.current?.focus();
      }
      focusedControl.current = null;
    }
  });

  const flowControls = (
    <div className={`${styles.deliveryControls} ${styles.tapControls}`}>
      <label htmlFor="titration-flow">
        Tap setting: {titrationTapSettingLabel(flow)}
      </label>
      <button
        type="button"
        aria-label="Set for individual drops"
        onClick={() => onDispatch({ type: "SET_FLOW", fraction: 0.025 })}
      >
        Set for drops
      </button>
      <input
        id="titration-flow"
        type="range"
        aria-valuetext={titrationTapSettingLabel(flow)}
        min="1"
        max="100"
        step="0.5"
        value={Number((flow * 100).toFixed(1))}
        onChange={(event) =>
          onDispatch({
            type: "SET_FLOW",
            fraction: Number(event.target.value) / 100,
          })
        }
      />
      <div className={styles.tapRangeLabels} aria-hidden="true">
        <span>Small opening</span>
        <span>Wide opening</span>
      </div>
    </div>
  );

  return (
    <section
      className={styles.card}
      onFocusCapture={(event) => {
        focusedControl.current = event.target;
      }}
      aria-label="Guided titration"
      data-guided-step={definition.id}
      data-review-mode={reviewMode ? "true" : "false"}
    >
      <h2 ref={headingRef} tabIndex={-1} className={styles.title}>
        {definition.title}
      </h2>
      {definition.id !== "intro" && definition.id !== "calculate" ? (
        <p className={styles.stepLabel}>
          {definition.ordinal
            ? `${definition.eyebrow} · Step ${definition.ordinal} of ${definition.total}`
            : definition.eyebrow}
        </p>
      ) : null}
      {reviewMode ? (
        <p className={styles.reviewNotice}>
          Reviewing earlier instructions. This does not undo your work.
        </p>
      ) : null}

      <p className={styles.body} data-guided-instruction>
        {showCalculation
          ? "Calculated from your concordant titres."
          : definition.body}
      </p>

      {reviewMode && definition.supportingBody ? (
        <p className={styles.techniqueNote}>{definition.supportingBody}</p>
      ) : null}

      {!reviewMode && state.session.swirled ? (
        <p className={styles.mixedState}>
          Solution mixed. Check the flask colour before adding more.
        </p>
      ) : null}

      {!reviewMode &&
      (definition.id === "stop-tap" || definition.id === "record-titre") ? (
        <dl className={styles.results}>
          <dt>Burette reading</dt>
          <dd>{readingMl.toFixed(2)} mL</dd>
        </dl>
      ) : null}

      {showCalculation ? (
        <dl
          className={styles.results}
          aria-label="Titration results"
          aria-live="polite"
        >
          <dt>Mean concordant titre</dt>
          <dd>{calculation.meanMl.toFixed(2)} mL</dd>
          <dt>Unknown HCl concentration</dt>
          <dd>{calculation.concentrationM.toFixed(4)} mol dm⁻³</dd>
        </dl>
      ) : null}

      {reviewMode ? (
        <div className={styles.reviewActions}>
          <button
            type="button"
            className={styles.returnCurrent}
            data-secondary-action="return-current"
            onClick={() => onDispatch({ type: "RETURN_TO_CURRENT" })}
          >
            Return to current step
          </button>

          <button
            type="button"
            data-secondary-action="back"
            onClick={() => onDispatch({ type: "BACK_REVIEW" })}
            disabled={state.reviewIndex === 0}
          >
            Previous
          </button>
          <button
            type="button"
            data-secondary-action="forward"
            onClick={() => onDispatch({ type: "FORWARD_REVIEW" })}
          >
            Next
          </button>
        </div>
      ) : (
        <>
          {definition.id === TitrationGuidedStep.OPEN_TAP ? flowControls : null}
          {!showCalculation &&
          definition.primaryLabel &&
          definition.primaryAction ? (
            <button
              type="button"
              className={styles.primaryAction}
              data-primary-action
              onClick={() => onDispatch(definition.primaryAction!)}
            >
              {definition.primaryLabel}
            </button>
          ) : null}
          {definition.id === TitrationGuidedStep.STOP_TAP ? flowControls : null}
          {definition.id === TitrationGuidedStep.OPEN_TAP && !reviewMode ? (
            <div className={styles.deliveryControls}>
              <button
                type="button"
                onClick={() => onDispatch({ type: "ONE_DROP" })}
              >
                Add one drop (0.05 mL)
              </button>
              <p>
                Modelled drop size. Click the amber stopcock to open or close
                it.
              </p>
            </div>
          ) : null}
          {definition.supportingBody ? (
            <p className={styles.techniqueNote}>{definition.supportingBody}</p>
          ) : null}
          {latestCheckpoint ? (
            <div className={styles.secondaryActions}>
              {state.session.tapOpenFraction <= 0 ? (
                <button
                  type="button"
                  data-secondary-action="back"
                  onClick={() => onDispatch({ type: "BACK_REVIEW" })}
                >
                  Review steps
                </button>
              ) : null}
              <button
                type="button"
                data-secondary-action="undo"
                onClick={() => onDispatch({ type: "UNDO" })}
              >
                Undo {latestCheckpoint.label}
              </button>
            </div>
          ) : null}
        </>
      )}
      {!reviewMode && (onReadMeniscus || onPracticeReading) ? (
        <div className={styles.readingTools}>
          <p className={styles.toolsLabel}>Reading tools</p>
          <div className={styles.readingActions}>
            {onReadMeniscus && !reviewMode ? (
              <button
                type="button"
                className={styles.readButton}
                disabled={state.session.tapOpenFraction > 0}
                onClick={onReadMeniscus}
              >
                Read meniscus at eye level
              </button>
            ) : null}
            {onPracticeReading && !reviewMode ? (
              <button
                type="button"
                className={styles.readButton}
                disabled={state.session.tapOpenFraction > 0}
                onClick={onPracticeReading}
              >
                Practise a hidden reading
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {!reviewMode ? (
        <dl className={styles.reagents} aria-label="Reagents">
          <div>
            <dt>Burette</dt>
            <dd>0.1000 M NaOH</dd>
          </div>
          <div>
            <dt>Flask</dt>
            <dd>
              Unknown HCl
              {state.session.indicatorAdded ? " + phenolphthalein" : ""}
            </dd>
          </div>
        </dl>
      ) : null}
    </section>
  );
}
